// @flow

import React, {
  forwardRef,
  useState,
  useEffect,
  useImperativeHandle,
} from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";

import { useDebounce } from "react-use";

import { not, includes, isEmpty } from "ramda";

import { useI18n } from "@quantum/i18n";
import { ClickOutside, Spinner } from "@quantum/ui";

import { useBuscaAtivosSelecionaveis } from "../../../../remote";
import { getModoApp } from "../../../../../state";

import { MODO_APP_SERIES } from "../../../../../model";
import FiltroAtivos from "./filtroAtivos";
import ResultadoAtivos, {
  tipoGrupoAtivosPermitidos,
  tipoGrupoAtivosPermitidosSeries,
} from "./resultadoAtivos";

import IconeFiltro from "../../../../../assets/icon_filter.png";

import "./buscar-ativo-selecionaveis.css";

const useDebouncedValue = (value, time = 2000) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  const [, cancel] = useDebounce(() => setDebouncedValue(value), time, [value]);
  return [debouncedValue, cancel, setDebouncedValue];
};

type Props = {
  +inputRef?: any,
  selecionarAtivo: any,
  setarInputAoSelecionarAtivo: any,
  +placeholder?: ?string,
};

const BuscarAtivosSelecionaveis = forwardRef<Props, any>(
  (
    {
      inputRef,
      placeholder,
      selecionarAtivo,
      setarInputAoSelecionarAtivo,
    }: Props,
    ref: any
  ) => {
    const { translate } = useI18n();
    const modoApp = useSelector(getModoApp);

    const gruposPermitidos =
      modoApp === MODO_APP_SERIES
        ? tipoGrupoAtivosPermitidosSeries
        : tipoGrupoAtivosPermitidos;

    const [query, setQuery] = useState("");
    const [inputValue, setInputValue] = useState(query);
    const [queryFilter, setQueryFilter] = useState("defaultSearch");

    const [mostrarResultados, setMostrarResultados] = useState(false);
    const [mostrarFiltros, setMostrarFiltros] = useState(false);

    const [ativosBusca, setAtivosBusca] = useState([]);

    const [debouncedQuery, cancel, setDebouncedQuery] = useDebouncedValue(
      query
    );

    const onChange = event => {
      if (setarInputAoSelecionarAtivo) setInputValue(event.target.value);
      setQuery(event.target.value);
      setAtivosBusca([]);
      ocultarPaineis();
    };

    const onKeyDown = React.useCallback(
      e => {
        if (e.keyCode === 13 || e.which === 13) {
          cancel();
          setDebouncedQuery(e.currentTarget.value);
        }
      },
      [setDebouncedQuery, cancel]
    );

    const onClickInput = () => {
      if (not(isEmpty(ativosBusca)) || loaded) {
        setMostrarResultados(true);
      }
    };

    const ocultarPaineis = () => {
      setMostrarResultados(false);
      setMostrarFiltros(false);
    };

    const abrirFiltros = () => {
      setMostrarFiltros(true);
    };

    const ocultarFiltros = () => {
      setMostrarFiltros(false);
      if (loading) {
        setMostrarResultados(false);
      }
    };

    const aoSelecionarAtivo = ativo => {
      selecionarAtivo(ativo);
      if (setarInputAoSelecionarAtivo) setInputValue(ativo.nome);
      ocultarPaineis();
    };

    const resetBusca = () => {
      setDebouncedQuery("");
      setQuery("");
      setAtivosBusca([]);
      if (setarInputAoSelecionarAtivo) {
        setInputValue("");
      }
    };

    useImperativeHandle(ref, () => ({
      resetBusca,
    }));

    const {
      data: options = ativosBusca,
      loading,
      loaded,
      error,
    } = useBuscaAtivosSelecionaveis({
      properties: { query: debouncedQuery, filtroBusca: queryFilter },
      fetch: Boolean(debouncedQuery) && debouncedQuery.length >= 3,
    });

    useEffect(() => {
      if (options.length) {
        const grupoAtivos = options
          .filter(
            ({ tipoItemSelecionavel }) =>
              ![
                "EMPRESA_ABERTA",
                "EMPRESA_AMERICANA",
                "EMPRESA_FECHADA",
              ].includes(tipoItemSelecionavel)
          )
          .filter(({ codigoGrupo }) =>
            includes(String(codigoGrupo), gruposPermitidos)
          )
          .reduce((grupo, item) => {
            grupo[item.codigoGrupo] = [
              ...(grupo[item.codigoGrupo] || []),
              item,
            ];
            return grupo;
          }, []);

        setAtivosBusca(grupoAtivos);
        setMostrarResultados(true);
      } else if (loaded && (debouncedQuery || error)) {
        setAtivosBusca([]);
        setMostrarResultados(true);
      }
    }, [options, loaded, debouncedQuery, error, gruposPermitidos]);

    return (
      <ClickOutside
        className="BuscarAtivosSelecionaveis"
        onClickOutside={ocultarPaineis}
      >
        <div className="BuscarAtivosSelecionaveis__input-container">
          <div
            onClick={abrirFiltros}
            className="BuscarAtivosSelecionaveis__icon BuscarAtivosSelecionaveis__icon--filter"
          >
            <img src={IconeFiltro} alt="" />
          </div>

          <input
            type="text"
            autoFocus
            className={classNames("BuscarAtivosSelecionaveis__input", {
              "BuscarAtivosSelecionaveis__input--uppercase": query.length > 0,
            })}
            value={setarInputAoSelecionarAtivo ? inputValue : query}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onClick={onClickInput}
            placeholder={
              placeholder ?? translate("buscaGlobal.placeHolder.defaultSearch")
            }
            ref={inputRef}
            disabled={loading}
          />
          {loading && (
            <div className="BuscarAtivosSelecionaveis__loading">
              <Spinner />
            </div>
          )}
        </div>

        <CSSTransition
          timeout={300}
          in={mostrarResultados}
          classNames="BuscarAtivosSelecionaveis__resultado"
          unmountOnExit
        >
          <ResultadoAtivos results={ativosBusca} onSelect={aoSelecionarAtivo} />
        </CSSTransition>

        <FiltroAtivos
          selectFilter={setQueryFilter}
          value={queryFilter}
          show={mostrarFiltros}
          onHide={ocultarFiltros}
        />
      </ClickOutside>
    );
  }
);

export default BuscarAtivosSelecionaveis;
